import type { Addresses as Address } from '@/types/pbSchema'

const FIELDS: (keyof Address)[] = [
    'line1',
    'line2',
    'city',
    'stateProvinceCounty',
    'zipOrPostcode',
    'country',
]

export function isValidAddress(address: unknown): address is Address {
    if (!address || typeof address !== 'object') return false

    const addr = address as Record<string, unknown>

    return (
        typeof addr.line1 === 'string' &&
        typeof addr.line2 === 'string' &&
        typeof addr.city === 'string' &&
        typeof addr.stateProvinceCounty === 'string' &&
        typeof addr.zipOrPostcode === 'string' &&
        typeof addr.country === 'string'
    )
}

export function isPartialAddress(address: unknown): address is Partial<Address> {
    if (!address || typeof address !== 'object') return false

    const addr = address as Record<string, unknown>

    for (const field of FIELDS) {
        if (field in addr && typeof addr[field] !== 'string') {
            return false
        }
    }

    return true
}

export function isAddressBlank(address: unknown): boolean {
    if (!address) return true

    if (!isPartialAddress(address)) {
        return true
    }

    return FIELDS.every(field => {
        return !address[field]
    })
}

export function isNonBlankAddress(address: unknown): address is Address {
    return isValidAddress(address) && !isAddressBlank(address)
}

export function hasExpandedAddress(obj: unknown): obj is { expand: { address: Partial<Address> } } {
    if (!obj || typeof obj !== 'object') return false

    const record = obj as Record<string, unknown>

    if (!record.expand || typeof record.expand !== 'object') return false

    const expand = record.expand as Record<string, unknown>

    return isPartialAddress(expand.address) && !isAddressBlank(expand.address)
}

export function formatAddress(address: unknown): string {
    if (!isNonBlankAddress(address)) return ''

    const parts = [
        address.line1,
        address.line2,
        address.city,
        address.stateProvinceCounty,
        address.zipOrPostcode,
        address.country,
    ].filter(Boolean)

    return parts.join(', ')
}
